/**
 * /guitar/fret-diatonic — Diatonic Triads on the Neck (rung 6).
 *
 * A key-establishing cadence plays, then a diatonic chord (I, ii, IV, V, vi).
 * The CAGED shape region lights up with 3rd + 5th given; user taps the root.
 * Requires the diatonic-triads MDX lesson before drilling.
 *
 * Filters: degree (I / ii / IV / V / vi / Mixed) + shape (E/A/G/C/D/Mixed).
 *
 * See docs/design/fret-diatonic-triads.md.
 */

import { useState, useMemo, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  expandNode,
  MVP_NODES,
  EXERCISE_SKILL_NODES,
  type Item,
} from "@music-app/learning-system";
import { usePrefsStore } from "@music-app/persistence";
import { ModeSwitcherBar, MODE_COLOR, type ExerciseMode } from "../components/ModeSwitcherBar.js";
import { GuitarDrillCore, GuitarNodeStats } from "./FretboardExercise.js";
import { PageHeader } from "../components/PageHeader.js";
import { buttonClasses } from "../components/Button.js";

// ─── Item pool ────────────────────────────────────────────────────────────────

const NODE_IDS = EXERCISE_SKILL_NODES["fret-diatonic"];
const ALL_ITEMS: Item[] = NODE_IDS.flatMap((id) =>
  expandNode(MVP_NODES.find((n) => n.id === id)!),
);

const DIATONIC_STAT_ROWS = [{ nodeId: "fret-diatonic-triads", label: "" }] as const;

type DegreeFilter = "I" | "ii" | "IV" | "V" | "vi" | "mixed";
type CagedShape = "E" | "A" | "G" | "C" | "D";

const DEGREES: DegreeFilter[] = ["I", "ii", "IV", "V", "vi", "mixed"];
const CAGED_SHAPES: CagedShape[] = ["E", "A", "G", "C", "D"];

const DEGREE_LABELS: Record<DegreeFilter, string> = {
  I: "I", ii: "ii", IV: "IV", V: "V", vi: "vi", mixed: "Mixed",
};
const CAGED_SHAPE_LABELS: Record<CagedShape, string> = {
  E: "E-shape", A: "A-shape", G: "G-shape", C: "C-shape", D: "D-shape",
};

const SESSION_LENGTHS = [5, 10, 15, 20, 30];
const DEFAULT_SESSION_LENGTH = 10;

// ─── Page ─────────────────────────────────────────────────────────────────────

export function FretDiatonicExercisePage(): ReactNode {
  const [searchParams] = useSearchParams();
  const rawMode = searchParams.get("mode") as ExerciseMode | null;
  const mode: Exclude<ExerciseMode, "challenge"> =
    rawMode === "listen" || rawMode === "review" ? rawMode : "practice";
  const from = searchParams.get("from") ?? undefined;
  const coached = searchParams.get("coached") === "true";

  const [sessionLength, setSessionLength] = useState(DEFAULT_SESSION_LENGTH);
  const [degreeFilter, setDegreeFilter] = useState<DegreeFilter>("mixed");
  const [selectedShapes, setSelectedShapes] = useState<Set<CagedShape>>(new Set(CAGED_SHAPES));

  function toggleShape(s: CagedShape | "all") {
    if (s === "all") { setSelectedShapes(new Set(CAGED_SHAPES)); return; }
    setSelectedShapes((prev) => {
      const next = new Set(prev);
      if (next.has(s)) { next.delete(s); if (next.size === 0) return new Set(CAGED_SHAPES); }
      else { next.add(s); }
      return next;
    });
  }

  const nodeItems = useMemo(() => {
    let items = ALL_ITEMS;
    if (degreeFilter !== "mixed") items = items.filter((i) => i.params["degree"] === degreeFilter);
    if (selectedShapes.size < CAGED_SHAPES.length) items = items.filter((i) => selectedShapes.has(i.params["shape"] as CagedShape));
    return items.length > 0 ? items : ALL_ITEMS;
  }, [degreeFilter, selectedShapes]);

  return (
    <GuitarDrillCore
      skillId="fret-diatonic"
      nodeItems={nodeItems}
      exercisePath="/guitar/fret-diatonic"
      drillTitle="Diatonic Triads"
      mode={mode}
      from={from}
      coached={coached}
      sessionLength={sessionLength}
      renderSetupScreen={(onStart, ready) => (
        <SetupScreen
          mode={mode}
          from={from}
          degreeFilter={degreeFilter}
          onDegreeFilter={setDegreeFilter}
          selectedShapes={selectedShapes}
          onToggleShape={toggleShape}
          sessionLength={sessionLength}
          onSessionLength={setSessionLength}
          ready={ready}
          onStart={onStart}
        />
      )}
    />
  );
}

// ─── Setup screen ─────────────────────────────────────────────────────────────

function SetupScreen({
  mode,
  from,
  degreeFilter,
  onDegreeFilter,
  selectedShapes,
  onToggleShape,
  sessionLength,
  onSessionLength,
  ready,
  onStart,
}: {
  mode: Exclude<ExerciseMode, "challenge">;
  from?: string;
  degreeFilter: DegreeFilter;
  onDegreeFilter: (d: DegreeFilter) => void;
  selectedShapes: Set<CagedShape>;
  onToggleShape: (s: CagedShape | "all") => void;
  sessionLength: number;
  onSessionLength: (n: number) => void;
  ready: boolean;
  onStart: () => void;
}): ReactNode {
  const isLessonCompleted = usePrefsStore((s) => s.isLessonCompleted);
  const lessonDone = isLessonCompleted("fret-diatonic-triads");
  const modeColor = MODE_COLOR[mode];

  const ACTIVE   = { borderColor: modeColor, backgroundColor: modeColor, color: "#FAF6EE" };
  const INACTIVE = { borderColor: "rgba(26,24,19,0.2)", backgroundColor: "#FAF6EE", color: "#1A1813" };
  const [showCustomLength, setShowCustomLength] = useState(false);

  const backLink =
    from === "today" ? { label: "← Today", href: "/" } :
    from === "learn" ? { label: "← Learn", href: "/learn" } :
    { label: "← Guitar Hub", href: "/guitar" };

  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <PageHeader
        back={backLink}
        title="Diatonic Triads"
        subhead="Hear the key — identify the chord's root in the CAGED shape."
      />

      <ModeSwitcherBar current={mode} exercisePath="/guitar/fret-diatonic" />

      <GuitarNodeStats rows={DIATONIC_STAT_ROWS} />

      {/* Lesson gate */}
      {!lessonDone && (
        <div className="mb-6 p-4 border border-accent/30 bg-paperWarm">
          <p className="font-serif italic text-sm text-ink mb-3">
            Read the lesson first — it explains how CAGED positions relate to
            diatonic chord roots.
          </p>
          <Link
            to="/learn/fret-diatonic-triads"
            className="font-mono text-[10px] uppercase tracking-[0.18em] text-accent hover:text-ink transition-colors"
          >
            Read lesson →
          </Link>
        </div>
      )}

      {/* Degree selector */}
      <section className="mb-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">
          Degree
        </p>
        <div className="flex flex-wrap gap-2">
          {DEGREES.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => onDegreeFilter(d)}
              aria-pressed={degreeFilter === d}
              className="px-3 py-1.5 font-mono text-xs border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
              style={degreeFilter === d ? ACTIVE : INACTIVE}
            >
              {DEGREE_LABELS[d]}
            </button>
          ))}
        </div>
      </section>

      {/* Shape selector */}
      <section className="mb-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">
          Shape
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => onToggleShape("all")}
            aria-pressed={selectedShapes.size === CAGED_SHAPES.length}
            className="px-3 py-1.5 font-mono text-xs border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
            style={selectedShapes.size === CAGED_SHAPES.length ? ACTIVE : INACTIVE}
          >
            All
          </button>
          {CAGED_SHAPES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => onToggleShape(s)}
              aria-pressed={selectedShapes.has(s)}
              className="px-3 py-1.5 font-mono text-xs border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
              style={selectedShapes.has(s) ? ACTIVE : INACTIVE}
            >
              {CAGED_SHAPE_LABELS[s]}
            </button>
          ))}
        </div>
      </section>

      <section className="mb-8">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">
          Exercises per session
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          {SESSION_LENGTHS.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => { onSessionLength(n); setShowCustomLength(false); }}
              aria-pressed={!showCustomLength && sessionLength === n}
              className="px-4 py-1.5 font-mono text-xs border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
              style={
                !showCustomLength && sessionLength === n
                  ? { borderColor: modeColor, backgroundColor: modeColor, color: "#FAF6EE" }
                  : { borderColor: "rgba(26,24,19,0.2)", backgroundColor: "#FAF6EE", color: "#1A1813" }
              }
            >
              {n}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setShowCustomLength(true)}
            aria-pressed={showCustomLength}
            className="px-4 py-1.5 font-mono text-xs border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
            style={showCustomLength ? { borderColor: modeColor, backgroundColor: modeColor, color: "#FAF6EE" } : { borderColor: "rgba(26,24,19,0.2)", backgroundColor: "#FAF6EE", color: "#1A1813" }}
          >
            Other…
          </button>
          {showCustomLength && (
            <input
              type="number"
              min={1}
              max={50}
              value={sessionLength}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                if (!isNaN(v) && v >= 1 && v <= 50) onSessionLength(v);
              }}
              className="w-16 px-3 py-1.5 font-mono text-xs border border-ink/20 bg-paper text-ink text-center focus:outline-none focus:border-ink"
              aria-label="Custom number of exercises"
              autoFocus
            />
          )}
        </div>
      </section>

      <button
        type="button"
        onClick={onStart}
        disabled={!ready}
        aria-busy={!ready}
        className={`mt-8 ${buttonClasses("primary")}`}
        style={{ backgroundColor: MODE_COLOR[mode] }}
      >
        {ready ? "Start →" : "Loading sounds…"}
      </button>
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mt-4">
        {ready ? "Audio starts on tap." : "Preparing the instrument…"}
      </p>
    </div>
  );
}
